<template>
  <div>
    <material-card
      icon="mdi-chip"
      icon-small
      color="primary"
      title="ESP32 OTA Firmware"
    >
      <v-card-text>
        <v-tabs v-model="tab">
          <v-tab>Firmware</v-tab>
          <v-tab>ESP Devices</v-tab>
          <v-tab>Remote Configs</v-tab>
          <v-tab>Update Records</v-tab>
          <v-tab>Taxi Fares</v-tab>
          <v-tab>MQTT Dashboard</v-tab>
          <v-tab v-feature-key="{ key: 'esp32Ota.mqttServer', label: 'MQTT Server' }">
            MQTT Server
          </v-tab>
        </v-tabs>

        <v-tabs-items v-model="tab">
          <!-- Firmware tab -->
          <v-tab-item>
            <v-row
              class="mt-4"
              align="center"
            >
              <v-col
                cols="12"
                sm="4"
                md="3"
              >
                <v-autocomplete
                  v-model="firmwareFilter.companyId"
                  :items="companies"
                  item-text="pathStr"
                  item-value="id"
                  label="Company (blank = global)"
                  clearable
                  hide-details
                  :loading="companiesLoading"
                  @focus="ensureCompanyDropdownCompanies"
                  @change="loadFirmware"
                />
              </v-col>
              <v-col
                cols="12"
                sm="4"
                md="3"
              >
                <v-text-field
                  v-model="firmwareFilter.productName"
                  label="Product Name"
                  placeholder="esp32s3"
                  clearable
                  hide-details
                  @change="loadFirmware"
                />
              </v-col>
              <v-col
                cols="12"
                sm="4"
                md="2"
              >
                <v-switch
                  v-model="firmwareFilter.includeInactive"
                  label="Include Inactive"
                  hide-details
                  @change="loadFirmware"
                />
              </v-col>
              <v-col
                cols="12"
                md="4"
                class="text-md-right"
              >
                <app-btn @click="openUploadDialog">
                  <v-icon>mdi-cloud-upload-outline</v-icon> Upload Firmware
                </app-btn>
              </v-col>
            </v-row>

            <v-divider class="mt-3" />

            <data-table-wrapper
              :headers="firmwareHeaders"
              :items="firmwareItems"
              :items-per-page="10"
            >
              <template v-slot:[`item.fileName`]="{ item }">
                <a
                  v-if="item.downloadUrl"
                  :href="item.downloadUrl"
                  target="_blank"
                  rel="noopener"
                >
                  {{ item.fileName }}
                </a>
                <span v-else>{{ item.fileName }}</span>
              </template>

              <template v-slot:[`item.sizeBytes`]="{ item }">
                {{ formatBytes(item.sizeBytes) }}
              </template>

              <template v-slot:[`item.companyId`]="{ item }">
                {{ item.companyId ? companyName(item.companyId) : 'Global' }}
              </template>

              <template v-slot:[`item.isActive`]="{ item }">
                <v-switch
                  v-model="item.isActive"
                  hide-details
                  class="ma-0 pa-0"
                  @change="updateStatus(item, { isActive: item.isActive })"
                />
              </template>

              <template v-slot:[`item.isMandatory`]="{ item }">
                <v-switch
                  v-model="item.isMandatory"
                  hide-details
                  class="ma-0 pa-0"
                  @change="updateStatus(item, { isMandatory: item.isMandatory })"
                />
              </template>

              <template v-slot:[`item.actions`]="{ item }">
                <v-icon
                  medium
                  color="error"
                  @click="deleteFirmwareDialog.show(item)"
                >
                  mdi-delete
                </v-icon>
              </template>
            </data-table-wrapper>

            <user-prompt
              v-model="deleteFirmwareDialog"
              @confirm="deleteFirmware"
            />

            <v-dialog
              v-model="uploadDialog"
              max-width="600px"
              :fullscreen="$vuetify.breakpoint.smAndDown"
            >
              <v-card>
                <v-card-title>
                  <span class="text-h5">Upload ESP32 OTA Firmware</span>
                </v-card-title>
                <v-card-text>
                  <v-form ref="uploadForm">
                    <v-row class="mt-2">
                      <v-col
                        cols="12"
                        sm="6"
                      >
                        <v-autocomplete
                          v-model="uploadItem.companyId"
                          :items="companies"
                          item-text="pathStr"
                          item-value="id"
                          label="Company (blank = global)"
                          clearable
                          :loading="companiesLoading"
                          @focus="ensureCompanyDropdownCompanies"
                        />
                      </v-col>
                      <v-col
                        cols="12"
                        sm="6"
                      >
                        <v-file-input
                          v-model="uploadItem.file"
                          label="Firmware (.bin)"
                          accept=".bin"
                          :rules="[rules.required]"
                          @change="handleFirmwareFileChange"
                        />
                      </v-col>
                      <v-col
                        cols="12"
                        sm="6"
                      >
                        <v-combobox
                          v-model="uploadItem.productName"
                          :items="productNameSuggestions"
                          label="Product Name"
                          placeholder="esp32s3"
                          clearable
                        />
                      </v-col>
                      <v-col
                        cols="12"
                        sm="6"
                      >
                        <v-combobox
                          v-model="uploadItem.hardware"
                          :items="hardwareSuggestions"
                          label="Hardware"
                          placeholder="ESP32-S3"
                          clearable
                        />
                      </v-col>
                      <v-col
                        cols="12"
                        sm="6"
                      >
                        <v-text-field
                          v-model="uploadItem.version"
                          label="Version"
                          placeholder="1.0.3"
                          :rules="[rules.required]"
                        />
                      </v-col>
                      <v-col
                        cols="12"
                        sm="6"
                      >
                        <v-text-field
                          v-model.number="uploadItem.versionCode"
                          label="Version Code"
                          type="number"
                          placeholder="103"
                          :rules="[rules.required, rules.positive]"
                        />
                      </v-col>
                      <v-col
                        cols="12"
                        sm="6"
                      >
                        <v-switch
                          v-model="uploadItem.isActive"
                          label="Active"
                          hide-details
                        />
                      </v-col>
                      <v-col
                        cols="12"
                        sm="6"
                      >
                        <v-switch
                          v-model="uploadItem.isMandatory"
                          label="Mandatory"
                          hide-details
                        />
                      </v-col>
                      <v-col cols="12">
                        <v-textarea
                          v-model="uploadItem.releaseNotes"
                          label="Release Notes"
                          rows="3"
                        />
                      </v-col>
                      <v-col
                        v-if="uploadProgress > 0"
                        cols="12"
                      >
                        <v-progress-linear
                          :value="uploadProgress"
                          height="6"
                          rounded
                          color="primary"
                        />
                        <div class="text-caption mt-1">
                          Uploading... {{ uploadProgress }}%
                        </div>
                      </v-col>
                    </v-row>
                  </v-form>
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn
                    color="blue darken-1"
                    text
                    :disabled="uploading"
                    @click="uploadDialog = false"
                  >
                    Cancel
                  </v-btn>
                  <v-btn
                    color="blue darken-1"
                    text
                    :loading="uploading"
                    @click="uploadFirmware"
                  >
                    Upload
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-tab-item>

          <!-- ESP devices tab -->
          <v-tab-item>
            <v-row
              class="mt-4"
              align="center"
            >
              <v-col
                cols="12"
                sm="6"
                md="3"
              >
                <v-text-field
                  v-model="deviceSearch"
                  append-icon="mdi-magnify"
                  label="Search devices"
                  clearable
                  hide-details
                />
              </v-col>
              <v-col
                cols="12"
                sm="6"
                md="3"
              >
                <v-autocomplete
                  v-model="deviceFilter.companyId"
                  :items="companies"
                  item-text="pathStr"
                  item-value="id"
                  label="Company"
                  clearable
                  hide-details
                  :loading="companiesLoading"
                  @focus="ensureCompanyDropdownCompanies"
                  @change="reloadDevices"
                />
              </v-col>
              <v-col
                cols="12"
                sm="6"
                md="3"
              >
                <v-text-field
                  v-model="deviceFilter.productName"
                  label="Product Name"
                  placeholder="esp32s3"
                  clearable
                  hide-details
                  @change="reloadDevices"
                />
              </v-col>
              <v-col
                cols="12"
                sm="6"
                md="3"
                class="text-md-right"
              >
                <app-btn @click="reloadDevices">
                  <v-icon>mdi-refresh</v-icon> Refresh
                </app-btn>
              </v-col>
            </v-row>

            <div class="esp-summary-grid mt-5">
              <div class="esp-summary-card">
                <v-icon color="primary">
                  mdi-chip
                </v-icon>
                <div>
                  <div class="text-caption text-uppercase grey--text">
                    Total Devices
                  </div>
                  <div class="text-h5 font-weight-medium">
                    {{ totalEspDevices }}
                  </div>
                </div>
              </div>
              <div class="esp-summary-card">
                <v-icon color="info">
                  mdi-progress-download
                </v-icon>
                <div>
                  <div class="text-caption text-uppercase grey--text">
                    Updating
                  </div>
                  <div class="text-h5 font-weight-medium">
                    {{ updatingDeviceCount }}
                  </div>
                </div>
              </div>
              <div class="esp-summary-card">
                <v-icon color="success">
                  mdi-check-decagram
                </v-icon>
                <div>
                  <div class="text-caption text-uppercase grey--text">
                    Up To Date
                  </div>
                  <div class="text-h5 font-weight-medium">
                    {{ upToDateDeviceCount }}
                  </div>
                </div>
              </div>
            </div>

            <v-divider class="mt-5" />

            <v-data-table
              :headers="deviceHeaders"
              :items="deviceItems"
              :options.sync="deviceOptions"
              :server-items-length="totalEspDevices"
              :loading="devicesLoading"
              :search.sync="deviceSearch"
              class="responsive-data-table esp-data-table"
              item-key="macAddress"
              :footer-props="{
                showFirstLastPage: true,
                'items-per-page-options': [10, 15, 25, 50],
              }"
            >
              <template v-slot:[`item.macAddress`]="{ item }">
                <div class="font-weight-medium">
                  {{ item.macAddress || '-' }}
                </div>
                <div class="text-caption grey--text">
                  {{ item.chipId || 'No chip id' }}
                </div>
              </template>

              <template v-slot:[`item.companyName`]="{ item }">
                {{ item.companyName || companyName(item.companyId) || '-' }}
              </template>

              <template v-slot:[`item.currentVersion`]="{ item }">
                <div class="version-stack">
                  <v-chip
                    x-small
                    color="grey lighten-3"
                    text-color="grey darken-3"
                  >
                    {{ item.currentVersion || '-' }}
                  </v-chip>
                  <v-icon
                    small
                    color="grey"
                  >
                    mdi-arrow-right
                  </v-icon>
                  <v-chip
                    x-small
                    color="primary"
                    text-color="white"
                  >
                    {{ item.targetVersion || '-' }}
                  </v-chip>
                </div>
              </template>

              <template v-slot:[`item.status`]="{ item }">
                <v-chip
                  small
                  :color="statusColor(item.status)"
                  text-color="white"
                >
                  {{ item.status || 'Unknown' }}
                </v-chip>
              </template>

              <template v-slot:[`item.reportedAt`]="{ item }">
                {{ formatDateTime(item.reportedAt) }}
              </template>

              <template v-slot:[`item.config`]="{ item }">
                <div class="text-caption">
                  <div>{{ item.taxiNumber || 'No taxi' }}</div>
                  <div class="grey--text">
                    {{ item.smsNumber || item.emergencyContactNumber || 'No contacts' }}
                  </div>
                </div>
              </template>

              <template v-slot:[`item.actions`]="{ item }">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      color="primary"
                      v-bind="attrs"
                      v-on="on"
                      @click="openConfigDialogForDevice(item)"
                    >
                      <v-icon small>
                        mdi-tune
                      </v-icon>
                    </v-btn>
                  </template>
                  <span>Add remote config</span>
                </v-tooltip>
              </template>
            </v-data-table>
          </v-tab-item>

          <!-- Remote configs tab -->
          <v-tab-item>
            <v-row
              class="mt-4"
              align="center"
            >
              <v-col
                cols="12"
                sm="6"
                md="3"
              >
                <v-text-field
                  v-model="configSearch"
                  append-icon="mdi-magnify"
                  label="Search configs"
                  clearable
                  hide-details
                />
              </v-col>
              <v-col
                cols="12"
                sm="6"
                md="3"
              >
                <v-autocomplete
                  v-model="configFilter.companyId"
                  :items="companies"
                  item-text="pathStr"
                  item-value="id"
                  label="Company"
                  clearable
                  hide-details
                  :loading="companiesLoading"
                  @focus="ensureCompanyDropdownCompanies"
                  @change="reloadConfigs"
                />
              </v-col>
              <v-col
                cols="12"
                sm="6"
                md="3"
              >
                <v-text-field
                  v-model="configFilter.deviceMac"
                  label="Device MAC"
                  clearable
                  hide-details
                  @change="reloadConfigs"
                />
              </v-col>
              <v-col
                cols="12"
                sm="6"
                md="3"
                class="text-md-right"
              >
                <app-btn @click="openConfigDialog">
                  <v-icon>mdi-plus</v-icon> New Config
                </app-btn>
              </v-col>
            </v-row>

            <v-divider class="mt-3" />

            <v-data-table
              :headers="configHeaders"
              :items="configItems"
              :options.sync="configOptions"
              :server-items-length="totalConfigs"
              :loading="configsLoading"
              :search.sync="configSearch"
              :expanded.sync="configExpanded"
              show-expand
              single-expand
              class="responsive-data-table esp-data-table"
              item-key="configId"
              :footer-props="{
                showFirstLastPage: true,
                'items-per-page-options': [10, 15, 25, 50],
              }"
            >
              <!-- Primary columns -->
              <template v-slot:[`item.configId`]="{ item }">
                {{ item.configId || item.id || '-' }}
              </template>

              <template v-slot:[`item.companyId`]="{ item }">
                {{ item.companyName || companyName(item.companyId) || '-' }}
              </template>

              <template v-slot:[`item.deviceMac`]="{ item }">
                <span class="font-weight-medium">{{ item.deviceMac || item.macAddress || '-' }}</span>
              </template>

              <template v-slot:[`item.taxiNumber`]="{ item }">
                {{ item.taxiNumber || '-' }}
              </template>

              <template v-slot:[`item.serverUrl`]="{ item }">
                <span class="text-caption server-url">{{ item.serverUrl || '-' }}</span>
              </template>

              <template v-slot:[`item.version`]="{ item }">
                <div class="text-caption">
                  <div>{{ item.versionName || '-' }}</div>
                  <div class="grey--text">code: {{ item.versionCode != null ? item.versionCode : '-' }}</div>
                </div>
              </template>

              <template v-slot:[`item.actions`]="{ item }">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      color="primary"
                      v-bind="attrs"
                      v-on="on"
                      @click="openConfigDialog(item)"
                    >
                      <v-icon small>mdi-pencil</v-icon>
                    </v-btn>
                  </template>
                  <span>Edit config</span>
                </v-tooltip>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      color="error"
                      v-bind="attrs"
                      v-on="on"
                      @click="deleteConfigDialog.show(item)"
                    >
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </template>
                  <span>Delete config</span>
                </v-tooltip>
              </template>

              <!-- Expanded detail panel -->
              <template v-slot:expanded-item="{ headers, item }">
                <td :colspan="headers.length" class="pa-0">
                  <v-sheet color="grey lighten-5" class="px-6 py-4">
                    <v-row dense>

                      <!-- Contacts -->
                      <v-col cols="12" sm="6" md="4">
                        <div class="text-caption font-weight-bold text-uppercase grey--text mb-1">Contacts</div>
                        <div class="text-body-2"><span class="grey--text">SMS:</span> {{ item.smsNumber || '-' }}</div>
                        <div class="text-body-2"><span class="grey--text">Driver:</span> {{ item.driverContactNumber || '-' }}</div>
                        <div class="text-body-2"><span class="grey--text">Emergency:</span> {{ item.emergencyContactNumber || '-' }}</div>
                      </v-col>

                      <!-- URLs -->
                      <v-col cols="12" sm="6" md="4">
                        <div class="text-caption font-weight-bold text-uppercase grey--text mb-1">URLs</div>
                        <div class="text-body-2 text-truncate"><span class="grey--text">MyDevices:</span> {{ item.mydevicesUrl || '-' }}</div>
                        <div class="text-body-2 text-truncate"><span class="grey--text">MyTaxi:</span> {{ item.mytaxiUrl || '-' }}</div>
                      </v-col>

                      <!-- API Keys -->
                      <v-col cols="12" sm="6" md="4">
                        <div class="text-caption font-weight-bold text-uppercase grey--text mb-1">API Keys</div>
                        <div class="text-body-2 text-truncate"><span class="grey--text">Geocode:</span> {{ item.geocodeApiKey || '-' }}</div>
                        <div class="text-body-2 text-truncate"><span class="grey--text">Directions:</span> {{ item.directionsApiKey || '-' }}</div>
                      </v-col>

                      <!-- OTA -->
                      <v-col cols="12" sm="6" md="4">
                        <div class="text-caption font-weight-bold text-uppercase grey--text mb-1 mt-3">OTA</div>
                        <div class="text-body-2"><span class="grey--text">Check interval:</span> {{ item.otaCheckIntervalSec != null ? item.otaCheckIntervalSec + 's' : '-' }}</div>
                        <div class="text-body-2"><span class="grey--text">Retry interval:</span> {{ item.otaRetryIntervalSec != null ? item.otaRetryIntervalSec + 's' : '-' }}</div>
                        <div class="text-body-2"><span class="grey--text">Max retries:</span> {{ item.otaMaxRetries != null ? item.otaMaxRetries : '-' }}</div>
                      </v-col>

                      <!-- Remote Config Polling -->
                      <v-col cols="12" sm="6" md="4">
                        <div class="text-caption font-weight-bold text-uppercase grey--text mb-1 mt-3">Remote Config Polling</div>
                        <div class="text-body-2"><span class="grey--text">Check interval:</span> {{ item.remoteConfigCheckIntervalSec != null ? item.remoteConfigCheckIntervalSec + 's' : '-' }}</div>
                        <div class="text-body-2"><span class="grey--text">Retry interval:</span> {{ item.remoteConfigRetryIntervalSec != null ? item.remoteConfigRetryIntervalSec + 's' : '-' }}</div>
                      </v-col>

                      <!-- Cellular / Network -->
                      <v-col cols="12" sm="6" md="4">
                        <div class="text-caption font-weight-bold text-uppercase grey--text mb-1 mt-3">Cellular / Network</div>
                        <div class="text-body-2"><span class="grey--text">APN:</span> {{ item.cellularApn || '-' }}</div>
                        <div class="text-body-2"><span class="grey--text">DNS 1:</span> {{ item.dns1 || '-' }}</div>
                        <div class="text-body-2"><span class="grey--text">DNS 2:</span> {{ item.dns2 || '-' }}</div>
                      </v-col>

                      <!-- Fare Backend -->
                      <v-col cols="12" sm="6" md="4">
                        <div class="text-caption font-weight-bold text-uppercase grey--text mb-1 mt-3">Fare Backend</div>
                        <div class="text-body-2 d-flex align-center gap-2">
                          <v-chip
                            x-small
                            :color="item.fareBackendEnabled ? 'success' : 'grey lighten-1'"
                            :text-color="item.fareBackendEnabled ? 'white' : 'grey darken-2'"
                          >
                            {{ item.fareBackendEnabled ? 'Enabled' : 'Disabled' }}
                          </v-chip>
                        </div>
                        <div class="text-body-2 mt-1"><span class="grey--text">Mode:</span> {{ item.fareBackendMode || '-' }}</div>
                      </v-col>

                      <!-- MQTT -->
                      <v-col cols="12" sm="6" md="4">
                        <div class="text-caption font-weight-bold text-uppercase grey--text mb-1 mt-3">MQTT</div>
                        <div class="text-body-2 text-truncate"><span class="grey--text">Broker:</span> {{ item.mqttBrokerUri || '-' }}</div>
                        <div class="text-body-2"><span class="grey--text">Username:</span> {{ item.mqttUsername || '-' }}</div>
                        <div class="text-body-2"><span class="grey--text">Password:</span> {{ item.mqttPassword ? '••••••••' : '-' }}</div>
                      </v-col>

                      <!-- Tracker Socket -->
                      <v-col cols="12" sm="6" md="4">
                        <div class="text-caption font-weight-bold text-uppercase grey--text mb-1 mt-3">Tracker Socket</div>
                        <div class="text-body-2"><span class="grey--text">IP:</span> {{ item.trackerSocketIp || '-' }}</div>
                        <div class="text-body-2"><span class="grey--text">Port:</span> {{ item.trackerSocketPort != null ? item.trackerSocketPort : '-' }}</div>
                      </v-col>

                      <!-- Debug -->
                      <v-col cols="12" sm="6" md="4">
                        <div class="text-caption font-weight-bold text-uppercase grey--text mb-1 mt-3">Debug</div>
                        <div class="text-body-2"><span class="grey--text">Test NVS:</span> {{ item.testNvs || '-' }}</div>
                      </v-col>

                    </v-row>
                  </v-sheet>
                </td>
              </template>
            </v-data-table>

            <user-prompt
              v-model="deleteConfigDialog"
              @confirm="deleteConfig"
            />
          </v-tab-item>

          <!-- Update records tab -->
          <v-tab-item>
            <v-row
              class="mt-4"
              align="center"
            >
              <v-col
                cols="12"
                sm="4"
                md="2"
              >
                <v-autocomplete
                  v-model="recordsFilter.companyId"
                  :items="companies"
                  item-text="pathStr"
                  item-value="id"
                  label="Company"
                  clearable
                  hide-details
                  :loading="companiesLoading"
                  @focus="ensureCompanyDropdownCompanies"
                  @change="loadRecords"
                />
              </v-col>
              <v-col
                cols="12"
                sm="4"
                md="2"
              >
                <v-text-field
                  v-model="recordsFilter.productName"
                  label="Product Name"
                  placeholder="esp32s3"
                  clearable
                  hide-details
                  @change="loadRecords"
                />
              </v-col>
              <v-col
                cols="12"
                sm="4"
                md="2"
              >
                <v-text-field
                  v-model="recordsFilter.macAddress"
                  label="MAC Address"
                  clearable
                  hide-details
                  @change="loadRecords"
                />
              </v-col>
              <v-col
                cols="12"
                sm="6"
                md="2"
              >
                <v-text-field
                  v-model="recordsFilter.chipId"
                  label="Chip Id"
                  clearable
                  hide-details
                  @change="loadRecords"
                />
              </v-col>
              <v-col
                cols="12"
                sm="6"
                md="2"
                class="text-md-right"
              >
                <app-btn @click="loadRecords">
                  <v-icon>mdi-refresh</v-icon> Refresh
                </app-btn>
              </v-col>
            </v-row>

            <v-divider class="mt-3" />

            <data-table-wrapper
              :headers="recordHeaders"
              :items="recordItems"
              :items-per-page="10"
            >
              <template v-slot:[`item.companyId`]="{ item }">
                {{ item.companyId ? companyName(item.companyId) : 'Global' }}
              </template>

              <template v-slot:[`item.reportedAt`]="{ item }">
                {{ $format.dateTime(item.reportedAt) }}
              </template>

              <template v-slot:[`item.status`]="{ item }">
                <v-chip
                  small
                  :color="statusColor(item.status)"
                  text-color="white"
                >
                  {{ item.status }}
                </v-chip>
              </template>
            </data-table-wrapper>
          </v-tab-item>

          <!-- Taxi fares tab -->
          <v-tab-item>
            <esp32-taxi-fares />
          </v-tab-item>

          <!-- MQTT server tab -->
          <v-tab-item>
            <mqtt-dashboard />
          </v-tab-item>

          <!-- MQTT server tab -->
          <v-tab-item>
            <esp32-mqtt-server
              :companies="companies"
              :companies-loading="companiesLoading"
              @load-companies="ensureCompanyDropdownCompanies"
            />
          </v-tab-item>
        </v-tabs-items>

        <v-dialog
          v-model="configDialog"
          max-width="900px"
          :fullscreen="$vuetify.breakpoint.smAndDown"
        >
          <v-card>
            <v-card-title>
              <span class="text-h5">{{ configDialogTitle }}</span>
            </v-card-title>
            <v-card-text>
              <v-form ref="configForm">

                <!-- ── Identity ───────────────────────────────────────────── -->
                <div class="text-subtitle-2 grey--text text--darken-1 mt-2 mb-1">Identity</div>
                <v-row>
                  <v-col cols="12" sm="6">
                    <v-autocomplete
                      v-model="configItem.companyId"
                      :items="companies"
                      item-text="pathStr"
                      item-value="id"
                      label="Company (blank = global)"
                      clearable
                      :loading="companiesLoading"
                      @focus="ensureCompanyDropdownCompanies"
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-combobox
                      v-model="configItem.deviceMac"
                      :items="deviceMacSuggestions"
                      label="Device MAC"
                      clearable
                      :rules="[rules.required]"
                      @change="onConfigDeviceMacChange"
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="configItem.taxiNumber"
                      label="Taxi Number"
                      clearable
                    />
                  </v-col>
                </v-row>

                <!-- ── Contacts ───────────────────────────────────────────── -->
                <div class="text-subtitle-2 grey--text text--darken-1 mt-3 mb-1">Contacts</div>
                <v-row>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="configItem.smsNumber"
                      label="SMS Number"
                      clearable
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="configItem.driverContactNumber"
                      label="Driver Contact Number"
                      clearable
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="configItem.emergencyContactNumber"
                      label="Emergency Contact Number"
                      clearable
                    />
                  </v-col>
                </v-row>

                <!-- ── Server URLs ─────────────────────────────────────────── -->
                <div class="text-subtitle-2 grey--text text--darken-1 mt-3 mb-1">Server URLs</div>
                <v-row>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="configItem.serverUrl"
                      label="Server URL"
                      clearable
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="configItem.mydevicesUrl"
                      label="MyDevices URL"
                      clearable
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="configItem.mytaxiUrl"
                      label="MyTaxi URL"
                      clearable
                    />
                  </v-col>
                </v-row>

                <!-- ── API Keys ────────────────────────────────────────────── -->
                <div class="text-subtitle-2 grey--text text--darken-1 mt-3 mb-1">API Keys</div>
                <v-row>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="configItem.geocodeApiKey"
                      label="Geocode API Key"
                      clearable
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="configItem.directionsApiKey"
                      label="Directions API Key"
                      clearable
                    />
                  </v-col>
                </v-row>

                <!-- ── OTA ────────────────────────────────────────────────── -->
                <div class="text-subtitle-2 grey--text text--darken-1 mt-3 mb-1">OTA</div>
                <v-row>
                  <v-col cols="12" sm="4">
                    <v-text-field
                      v-model.number="configItem.otaCheckIntervalSec"
                      label="Check Interval (sec)"
                      type="number"
                      clearable
                    />
                  </v-col>
                  <v-col cols="12" sm="4">
                    <v-text-field
                      v-model.number="configItem.otaRetryIntervalSec"
                      label="Retry Interval (sec)"
                      type="number"
                      clearable
                    />
                  </v-col>
                  <v-col cols="12" sm="4">
                    <v-text-field
                      v-model.number="configItem.otaMaxRetries"
                      label="Max Retries"
                      type="number"
                      clearable
                    />
                  </v-col>
                </v-row>

                <!-- ── Remote Config Polling ───────────────────────────────── -->
                <div class="text-subtitle-2 grey--text text--darken-1 mt-3 mb-1">Remote Config Polling</div>
                <v-row>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model.number="configItem.remoteConfigCheckIntervalSec"
                      label="Check Interval (sec)"
                      type="number"
                      clearable
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model.number="configItem.remoteConfigRetryIntervalSec"
                      label="Retry Interval (sec)"
                      type="number"
                      clearable
                    />
                  </v-col>
                </v-row>

                <!-- ── Cellular / Network ──────────────────────────────────── -->
                <div class="text-subtitle-2 grey--text text--darken-1 mt-3 mb-1">Cellular / Network</div>
                <v-row>
                  <v-col cols="12" sm="4">
                    <v-text-field
                      v-model="configItem.cellularApn"
                      label="Cellular APN"
                      clearable
                    />
                  </v-col>
                  <v-col cols="12" sm="4">
                    <v-text-field
                      v-model="configItem.dns1"
                      label="DNS 1"
                      clearable
                    />
                  </v-col>
                  <v-col cols="12" sm="4">
                    <v-text-field
                      v-model="configItem.dns2"
                      label="DNS 2"
                      clearable
                    />
                  </v-col>
                </v-row>

                <!-- ── Fare Backend ────────────────────────────────────────── -->
                <div class="text-subtitle-2 grey--text text--darken-1 mt-3 mb-1">Fare Backend</div>
                <v-row>
                  <v-col cols="12" sm="4" class="d-flex align-center">
                    <v-switch
                      v-model="configItem.fareBackendEnabled"
                      label="Enabled"
                      hide-details
                      class="mt-0"
                    />
                  </v-col>
                  <v-col cols="12" sm="8">
                    <v-text-field
                      v-model="configItem.fareBackendMode"
                      label="Fare Backend Mode"
                      clearable
                    />
                  </v-col>
                </v-row>

                <!-- ── MQTT ────────────────────────────────────────────────── -->
                <div class="text-subtitle-2 grey--text text--darken-1 mt-3 mb-1">MQTT</div>
                <v-row>
                  <v-col cols="12" sm="12">
                    <v-text-field
                      v-model="configItem.mqttBrokerUri"
                      label="MQTT Broker URI"
                      clearable
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="configItem.mqttUsername"
                      label="MQTT Username"
                      clearable
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="configItem.mqttPassword"
                      label="MQTT Password"
                      clearable
                    />
                  </v-col>
                </v-row>

                <!-- ── Tracker Socket ──────────────────────────────────────── -->
                <div class="text-subtitle-2 grey--text text--darken-1 mt-3 mb-1">Tracker Socket</div>
                <v-row>
                  <v-col cols="12" sm="8">
                    <v-text-field
                      v-model="configItem.trackerSocketIp"
                      label="Tracker Socket IP"
                      clearable
                    />
                  </v-col>
                  <v-col cols="12" sm="4">
                    <v-text-field
                      v-model.number="configItem.trackerSocketPort"
                      label="Tracker Socket Port"
                      type="number"
                      clearable
                    />
                  </v-col>
                </v-row>

                <!-- ── Version / Debug ────────────────────────────────────── -->
                <div class="text-subtitle-2 grey--text text--darken-1 mt-3 mb-1">Version / Debug</div>
                <v-row>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="configItem.versionName"
                      label="Version Name"
                      placeholder="1.0.0"
                      clearable
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model.number="configItem.versionCode"
                      label="Version Code"
                      type="number"
                      placeholder="100"
                      clearable
                    />
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="configItem.testNvs"
                      label="Test NVS"
                      clearable
                    />
                  </v-col>
                </v-row>

              </v-form>
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn
                color="blue darken-1"
                text
                :disabled="savingConfig"
                @click="configDialog = false"
              >
                Cancel
              </v-btn>
              <v-btn
                color="blue darken-1"
                text
                :loading="savingConfig"
                @click="saveConfig"
              >
                Save
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-card-text>
    </material-card>
  </div>
</template>

<script>
  import store from '../store'
  import Esp32MqttServer from './components/Esp32MqttServer.vue'
  import Esp32TaxiFares from './components/Esp32TaxiFares.vue'
  import MqttDashboard from './MqttDashboard.vue'

  export default {
    name: 'Esp32Ota',
    components: { Esp32MqttServer, Esp32TaxiFares, MqttDashboard },
    data: () => ({
      tab: 0,
      rules: {
        required: (value) => (value !== null && value !== undefined && value !== '') || 'Required.',
        positive: (value) => (Number(value) > 0) || 'Must be greater than 0.',
      },
      companiesLoading: false,
      companiesLoadedForDropdown: false,

      firmwareLoading: false,
      firmwareItems: [],
      firmwareFilter: {
        companyId: undefined,
        productName: undefined,
        includeInactive: false,
      },
      firmwareHeaders: [
        { text: 'Id', value: 'id' },
        { text: 'Company', value: 'companyId' },
        { text: 'Product', value: 'productName' },
        { text: 'Hardware', value: 'hardware' },
        { text: 'Version', value: 'version' },
        { text: 'Version Code', value: 'versionCode' },
        { text: 'File', value: 'fileName' },
        { text: 'Size', value: 'sizeBytes' },
        { text: 'Active', value: 'isActive' },
        { text: 'Mandatory', value: 'isMandatory' },
        { text: 'Actions', value: 'actions', align: 'end', sortable: false },
      ],

      uploadDialog: false,
      uploading: false,
      uploadProgress: 0,
      defaultProductNameSuggestions: [
        'esp32s3',
        'esp32',
        'esp32c3',
        'esp32c6',
      ],
      defaultHardwareSuggestions: [
        'ESP32',
        'ESP32-S3',
        'ESP32-C3',
        'ESP32-C6',
      ],
      uploadItem: {},
      defaultUploadItem: {
        companyId: undefined,
        productName: 'esp32s3',
        hardware: '',
        version: '',
        versionCode: undefined,
        isActive: true,
        isMandatory: false,
        releaseNotes: '',
        file: undefined,
      },

      deleteFirmwareDialog: null,

      devicesLoading: false,
      deviceItems: [],
      totalEspDevices: 0,
      deviceOptions: {},
      deviceSearch: '',
      deviceSearchTimer: undefined,
      deviceFilter: {
        companyId: undefined,
        productName: undefined,
      },
      deviceHeaders: [
        { text: 'Device', value: 'macAddress' },
        { text: 'Company', value: 'companyName' },
        { text: 'Product', value: 'productName' },
        { text: 'Version', value: 'currentVersion', sortable: false },
        { text: 'Status', value: 'status' },
        { text: 'Reported At', value: 'reportedAt' },
        { text: 'Config', value: 'config', sortable: false },
        { text: 'Actions', value: 'actions', align: 'end', sortable: false },
      ],

      configsLoading: false,
      configItems: [],
      totalConfigs: 0,
      configOptions: {},
      configSearch: '',
      configSearchTimer: undefined,
      configFilter: {
        companyId: undefined,
        deviceMac: undefined,
      },
      configHeaders: [
        { text: '', value: 'data-table-expand' },
        { text: 'Config Id', value: 'configId' },
        { text: 'Company', value: 'companyId' },
        { text: 'Device MAC', value: 'deviceMac' },
        { text: 'Taxi Number', value: 'taxiNumber' },
        { text: 'Server URL', value: 'serverUrl' },
        { text: 'Version', value: 'version', sortable: false },
        { text: 'Actions', value: 'actions', align: 'end', sortable: false },
      ],
      configDialog: false,
      savingConfig: false,
      configExpanded: [],
      configItem: {},
      defaultConfigItem: {
        id: undefined,
        configId: undefined,
        companyId: undefined,
        deviceMac: '',
        taxiNumber: '',
        // contacts
        smsNumber: '',
        driverContactNumber: '',
        emergencyContactNumber: '',
        // server urls
        serverUrl: 'https://mydevices.myweb.net.au',
        mydevicesUrl: '',
        mytaxiUrl: '',
        // api keys
        geocodeApiKey: '',
        directionsApiKey: '',
        // ota
        otaCheckIntervalSec: undefined,
        otaRetryIntervalSec: undefined,
        otaMaxRetries: undefined,
        // remote config polling
        remoteConfigCheckIntervalSec: undefined,
        remoteConfigRetryIntervalSec: undefined,
        // cellular / network
        cellularApn: '',
        dns1: '',
        dns2: '',
        // fare backend
        fareBackendEnabled: false,
        fareBackendMode: '',
        // mqtt
        mqttBrokerUri: '',
        mqttUsername: '',
        mqttPassword: '',
        // tracker socket
        trackerSocketIp: '',
        trackerSocketPort: undefined,
        // version / debug
        testNvs: '',
        versionName: '',
        versionCode: undefined,
      },
      deleteConfigDialog: null,

      recordsLoading: false,
      recordItems: [],
      recordsFilter: {
        companyId: undefined,
        deviceId: undefined,
        macAddress: undefined,
        chipId: undefined,
        productName: undefined,
      },
      recordHeaders: [
        { text: 'Id', value: 'id' },
        { text: 'Company', value: 'companyId' },
        { text: 'Device', value: 'deviceName' },
        { text: 'Chip Id', value: 'chipId' },
        { text: 'MAC Address', value: 'macAddress' },
        { text: 'Product', value: 'productName' },
        { text: 'Current Version', value: 'currentVersion' },
        { text: 'Target Version', value: 'targetVersion' },
        { text: 'Status', value: 'status' },
        { text: 'Message', value: 'message' },
        { text: 'Reported At', value: 'reportedAt' },
      ],
    }),

    computed: {
      companies () {
        return store.getters['company/getCompanies']
      },

      productNameSuggestions () {
        return this.uniqueSuggestions([
          ...this.defaultProductNameSuggestions,
          ...this.firmwareItems.map(item => item.productName),
          ...this.recordItems.map(item => item.productName),
          ...this.deviceItems.map(item => item.productName),
        ])
      },

      hardwareSuggestions () {
        return this.uniqueSuggestions([
          ...this.defaultHardwareSuggestions,
          ...this.firmwareItems.map(item => item.hardware),
        ])
      },

      deviceMacSuggestions () {
        return this.uniqueSuggestions([
          ...this.deviceItems.map(item => item.macAddress),
          ...this.configItems.map(item => item.deviceMac || item.macAddress),
        ])
      },

      updatingDeviceCount () {
        return this.deviceItems.filter(item => ['Downloading', 'Installing'].includes(item.status)).length
      },

      upToDateDeviceCount () {
        return this.deviceItems.filter(item => item.currentVersionCode && item.targetVersionCode && item.currentVersionCode >= item.targetVersionCode).length
      },

      configDialogTitle () {
        return this.configItem?.configId || this.configItem?.id ? 'Edit ESP32 Remote Config' : 'New ESP32 Remote Config'
      },
    },

    watch: {
      deviceOptions: {
        handler () {
          this.loadDevices()
        },
        deep: true,
      },

      configOptions: {
        handler () {
          this.loadConfigs()
        },
        deep: true,
      },

      deviceSearch () {
        clearTimeout(this.deviceSearchTimer)
        this.deviceSearchTimer = setTimeout(() => {
          this.reloadDevices()
        }, 500)
      },

      configSearch () {
        clearTimeout(this.configSearchTimer)
        this.configSearchTimer = setTimeout(() => {
          this.reloadConfigs()
        }, 500)
      },
    },

    created () {
      if (!this.$store.getters['authUser/isSuperUser']) {
        this.$toast.error('You do not have access to ESP32 OTA.', 'Error', { position: 'topRight' })
        this.$router.replace('/devices')
        return
      }

      this.uploadItem = Object.assign({}, this.defaultUploadItem)
      this.configItem = Object.assign({}, this.defaultConfigItem)
      this.ensureCompanyDropdownCompanies()
      this.loadFirmware()
      this.loadRecords()
    },

    methods: {
      async ensureCompanyDropdownCompanies () {
        if (this.companiesLoading || this.companiesLoadedForDropdown) {
          return
        }

        this.companiesLoading = true
        try {
          const queuedIds = new Set()
          const loadedIds = new Set()
          const rootCompanies = await store.dispatch('company/loadCompanies', 0)
          const queue = rootCompanies.map(company => company.id).filter(Boolean)

          queue.forEach(id => queuedIds.add(id))

          while (queue.length && loadedIds.size < 500) {
            const companyId = queue.shift()

            if (!companyId || loadedIds.has(companyId)) {
              continue
            }

            loadedIds.add(companyId)
            const existingCompany = store.state.company.companies[companyId]
            const existingChildren = existingCompany?.children || []
            const children = existingChildren.length
              ? existingChildren
              : await store.dispatch('company/loadCompanies', companyId)

            children.forEach(child => {
              if (child?.id && !queuedIds.has(child.id) && !loadedIds.has(child.id)) {
                queuedIds.add(child.id)
                queue.push(child.id)
              }
            })
          }

          this.companiesLoadedForDropdown = true
        } catch (error) {
          this.$toast.error(
            error?.response?.data?.message || error?.message || 'Could not load all companies.',
            'Error',
            { position: 'topRight' },
          )
        } finally {
          this.companiesLoading = false
        }
      },

      companyName (companyId) {
        if (!companyId || Number(companyId) === -1) {
          return 'Global'
        }

        const company = this.companies.find(c => c.id === companyId)
        return company ? company.pathStr : companyId
      },

      uniqueSuggestions (values) {
        const seen = {}

        return values
          .map(value => String(value || '').trim())
          .filter(value => {
            const key = value.toLowerCase()

            if (!value || seen[key]) {
              return false
            }

            seen[key] = true
            return true
          })
      },

      normalizeMac (value) {
        return String(value || '').trim().toLowerCase()
      },

      findDeviceByMac (deviceMac) {
        const normalizedMac = this.normalizeMac(deviceMac)

        if (!normalizedMac) {
          return null
        }

        return [
          ...this.deviceItems,
          ...this.configItems,
        ].find(item => this.normalizeMac(item.macAddress || item.deviceMac) === normalizedMac) || null
      },

      onConfigDeviceMacChange (deviceMac) {
        const matchedDevice = this.findDeviceByMac(deviceMac)

        if (!matchedDevice) {
          return
        }

        this.configItem.companyId = matchedDevice.companyId || undefined
      },

      formatBytes (bytes) {
        if (!bytes && bytes !== 0) {
          return ''
        }

        if (bytes < 1024) {
          return `${bytes} B`
        }

        const units = ['KB', 'MB', 'GB']
        let value = bytes / 1024
        let unitIndex = 0
        while (value >= 1024 && unitIndex < units.length - 1) {
          value /= 1024
          unitIndex += 1
        }

        return `${value.toFixed(1)} ${units[unitIndex]}`
      },

      statusColor (status) {
        switch (status) {
          case 'Installed':
          case 'Success':
          case 'Updated':
            return 'success'
          case 'Failed':
            return 'error'
          case 'Downloading':
          case 'Installing':
            return 'info'
          default:
            return 'grey'
        }
      },

      formatDateTime (value) {
        return value ? this.$format.dateTime(value) : '-'
      },

      buildPagedRequest (options, search, filters = {}) {
        const page = Number(options?.page || 1)
        const itemsPerPage = Number(options?.itemsPerPage || 10)

        return {
          page,
          limit: itemsPerPage,
          start: itemsPerPage * (page - 1),
          length: itemsPerPage,
          Start: itemsPerPage * (page - 1),
          Length: itemsPerPage,
          search: search || undefined,
          Search: search || undefined,
          ...filters,
        }
      },

      normalizePagedResponse (responseData, itemKeys, totalKeys) {
        const wrapper = responseData || {}
        const source = Array.isArray(wrapper.data) ? wrapper : (wrapper.data || wrapper)
        const items = itemKeys.reduce((result, key) => {
          return result || source?.[key]
        }, null) || (Array.isArray(source) ? source : [])
        const total = totalKeys.reduce((result, key) => {
          return result !== undefined && result !== null ? result : source?.[key] ?? wrapper?.[key]
        }, undefined)

        return {
          items,
          total: Number(total !== undefined && total !== null ? total : items.length),
        }
      },

      buildConfigGetAllRequest () {
        const page = Number(this.configOptions?.page || 1)
        const itemsPerPage = Number(this.configOptions?.itemsPerPage || 10)
        const sortColumnName = this.configOptions?.sortBy?.[0] || 'id'
        const sortDirection = this.configOptions?.sortDesc?.[0] ? 'desc' : 'asc'
        const fieldSearch = this.configFilter.deviceMac || (this.configFilter.companyId ? String(this.configFilter.companyId) : '')

        return {
          start: itemsPerPage * (page - 1),
          length: itemsPerPage,
          search: this.configSearch || fieldSearch || '',
          sortColumnName,
          sortDirection,
          searchField: this.configFilter.deviceMac ? 'deviceMac' : (this.configFilter.companyId ? 'companyId' : ''),
        }
      },

      reloadDevices () {
        if (this.deviceOptions.page === 1) {
          this.loadDevices()
          return
        }

        this.deviceOptions = {
          ...this.deviceOptions,
          page: 1,
        }
      },

      reloadConfigs () {
        if (this.configOptions.page === 1) {
          this.loadConfigs()
          return
        }

        this.configOptions = {
          ...this.configOptions,
          page: 1,
        }
      },

      async loadDevices () {
        this.devicesLoading = true
        try {
          const response = await this.$http.post('Esp32/GetAll', this.buildPagedRequest(
            this.deviceOptions,
            this.deviceSearch,
            {
              companyId: this.deviceFilter.companyId || undefined,
              productName: this.deviceFilter.productName || undefined,
            },
          ))
          const result = this.normalizePagedResponse(response.data, ['devices', 'items', 'data'], ['totalDevices', 'total', 'totalCount'])

          this.deviceItems = result.items
          this.totalEspDevices = result.total
        } catch (error) {
          this.$toast.error(
            error?.response?.data?.message || error?.message || 'Could not load ESP32 devices.',
            'Error',
            { position: 'topRight' },
          )
        } finally {
          this.devicesLoading = false
        }
      },

      async loadConfigs () {
        this.configsLoading = true
        try {
          const response = await this.$http.post('Esp32Config/GetAll', this.buildConfigGetAllRequest())
          const result = this.normalizePagedResponse(response.data, ['configs', 'remoteConfigs', 'devices', 'items', 'data'], ['totalConfigs', 'totalDevices', 'total', 'totalCount'])

          this.configItems = result.items
          this.totalConfigs = result.total
        } catch (error) {
          this.$toast.error(
            error?.response?.data?.message || error?.message || 'Could not load ESP32 configs.',
            'Error',
            { position: 'topRight' },
          )
        } finally {
          this.configsLoading = false
        }
      },

      openConfigDialog (item) {
        this.configItem = Object.assign({}, this.defaultConfigItem, item || {})
        this.configItem.deviceMac = this.configItem.deviceMac || this.configItem.macAddress || ''
        this.configItem.serverUrl = this.configItem.serverUrl || this.defaultConfigItem.serverUrl
        if (!item) {
          this.onConfigDeviceMacChange(this.configItem.deviceMac)
        }
        this.configDialog = true
        if (this.$refs.configForm) {
          this.$refs.configForm.resetValidation()
        }
      },

      openConfigDialogForDevice (device) {
        this.openConfigDialog({
          companyId: device.companyId,
          deviceMac: device.macAddress,
          taxiNumber: device.taxiNumber || '',
          smsNumber: device.smsNumber || '',
          emergencyContactNumber: device.emergencyContactNumber || '',
          serverUrl: device.serverUrl || this.defaultConfigItem.serverUrl,
          versionName: device.versionName || device.targetVersion || '',
          versionCode: device.versionCode || device.targetVersionCode || undefined,
        })
      },

      toNullableInt (value) {
        return value !== undefined && value !== null && value !== '' ? Number(value) : null
      },

      configPayload () {
        return {
          companyId: this.configItem.companyId || null,
          deviceMac: this.configItem.deviceMac,
          taxiNumber: this.configItem.taxiNumber || '',
          // contacts
          smsNumber: this.configItem.smsNumber || '',
          driverContactNumber: this.configItem.driverContactNumber || '',
          emergencyContactNumber: this.configItem.emergencyContactNumber || '',
          // server urls
          serverUrl: this.configItem.serverUrl || '',
          mydevicesUrl: this.configItem.mydevicesUrl || '',
          mytaxiUrl: this.configItem.mytaxiUrl || '',
          // api keys
          geocodeApiKey: this.configItem.geocodeApiKey || '',
          directionsApiKey: this.configItem.directionsApiKey || '',
          // ota
          otaCheckIntervalSec: this.toNullableInt(this.configItem.otaCheckIntervalSec),
          otaRetryIntervalSec: this.toNullableInt(this.configItem.otaRetryIntervalSec),
          otaMaxRetries: this.toNullableInt(this.configItem.otaMaxRetries),
          // remote config polling
          remoteConfigCheckIntervalSec: this.toNullableInt(this.configItem.remoteConfigCheckIntervalSec),
          remoteConfigRetryIntervalSec: this.toNullableInt(this.configItem.remoteConfigRetryIntervalSec),
          // cellular / network
          cellularApn: this.configItem.cellularApn || '',
          dns1: this.configItem.dns1 || '',
          dns2: this.configItem.dns2 || '',
          // fare backend
          fareBackendEnabled: this.configItem.fareBackendEnabled === true,
          fareBackendMode: this.configItem.fareBackendMode || '',
          // mqtt
          mqttBrokerUri: this.configItem.mqttBrokerUri || '',
          mqttUsername: this.configItem.mqttUsername || '',
          mqttPassword: this.configItem.mqttPassword || '',
          // tracker socket
          trackerSocketIp: this.configItem.trackerSocketIp || '',
          trackerSocketPort: this.toNullableInt(this.configItem.trackerSocketPort),
          // version / debug
          testNvs: this.configItem.testNvs || '',
          versionName: this.configItem.versionName || '',
          versionCode: this.toNullableInt(this.configItem.versionCode),
        }
      },

      async saveConfig () {
        if (!this.$refs.configForm.validate()) {
          return
        }

        this.savingConfig = true
        try {
          const payload = this.configPayload()
          const configId = this.configItem.configId || this.configItem.id
          const response = configId
            ? await this.$http.put(`Esp32Config/RemoteConfig/${configId}`, payload)
            : await this.$http.post('Esp32Config/RemoteConfig', payload, {
              headers: { 'Content-Type': 'application/json-patch+json' },
            })

          if (response.data?.success === false) {
            this.$toast.error(response.data?.message || 'Could not save ESP32 config.', 'Error', {
              position: 'topRight',
            })
            return
          }

          this.$toast.success(response.data?.message || 'ESP32 config saved successfully.', 'Success', {
            position: 'topRight',
          })
          this.configDialog = false
          this.loadConfigs()
          this.loadDevices()
        } catch (error) {
          this.$toast.error(
            error?.response?.data?.message || error?.message || 'Could not save ESP32 config.',
            'Error',
            { position: 'topRight' },
          )
        } finally {
          this.savingConfig = false
        }
      },

      async deleteConfig (item) {
        const configId = item?.configId || item?.id
        if (!configId) {
          this.$toast.error('Could not identify the selected ESP32 config.', 'Error', {
            position: 'topRight',
          })
          return
        }

        try {
          const response = await this.$http.delete(`Esp32Config/RemoteConfig/${configId}`)

          if (response.data?.success === false) {
            this.$toast.error(response.data?.message || 'Could not delete ESP32 config.', 'Error', {
              position: 'topRight',
            })
            return
          }

          this.$toast.success(response.data?.message || 'ESP32 config deleted successfully.', 'Success', {
            position: 'topRight',
          })
          this.loadConfigs()
          this.loadDevices()
        } catch (error) {
          this.$toast.error(
            error?.response?.data?.message || error?.message || 'Could not delete ESP32 config.',
            'Error',
            { position: 'topRight' },
          )
        }
      },

      async loadFirmware () {
        this.firmwareLoading = true
        try {
          const response = await this.$http.get('Esp32Ota/Updates', {
            params: {
              companyId: this.firmwareFilter.companyId || undefined,
              productName: this.firmwareFilter.productName || undefined,
              includeInactive: this.firmwareFilter.includeInactive,
            },
          })

          this.firmwareItems = response.data?.data || response.data || []
        } catch (error) {
          this.$toast.error('Could not load firmware list.', 'Error', {
            position: 'topRight',
          })
        } finally {
          this.firmwareLoading = false
        }
      },

      openUploadDialog () {
        this.uploadItem = Object.assign({}, this.defaultUploadItem)
        this.uploadProgress = 0
        this.uploadDialog = true
        if (this.$refs.uploadForm) {
          this.$refs.uploadForm.resetValidation()
        }
      },

      parseFirmwareFileName (fileName) {
        const nameWithoutExtension = String(fileName || '').replace(/\.[^.]+$/, '')
        const numberParts = nameWithoutExtension.match(/\d+/g) || []

        if (numberParts.length < 4) {
          return null
        }

        const versionParts = numberParts.slice(-4, -1)
        const versionCode = numberParts[numberParts.length - 1]

        return {
          version: versionParts.join('.'),
          versionCode: Number(versionCode),
        }
      },

      handleFirmwareFileChange (file) {
        const firmwareFile = Array.isArray(file) ? file[0] : file
        const parsedFirmware = this.parseFirmwareFileName(firmwareFile?.name)

        if (!parsedFirmware) {
          return
        }

        this.uploadItem.version = parsedFirmware.version
        this.uploadItem.versionCode = parsedFirmware.versionCode
      },

      async uploadFirmware () {
        if (!this.$refs.uploadForm.validate()) {
          return
        }

        const form = new FormData()
        if (this.uploadItem.companyId) {
          form.append('CompanyId', this.uploadItem.companyId)
        }
        if (this.uploadItem.productName) {
          form.append('ProductName', this.uploadItem.productName)
        }
        if (this.uploadItem.hardware) {
          form.append('Hardware', this.uploadItem.hardware)
        }
        form.append('Version', this.uploadItem.version)
        form.append('VersionCode', this.uploadItem.versionCode)
        form.append('IsActive', this.uploadItem.isActive)
        form.append('IsMandatory', this.uploadItem.isMandatory)
        if (this.uploadItem.releaseNotes) {
          form.append('ReleaseNotes', this.uploadItem.releaseNotes)
        }
        form.append('File', this.uploadItem.file)

        this.uploading = true
        this.uploadProgress = 0

        try {
          const response = await this.$http.post('Esp32Ota/Upload', form, {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (progressEvent) => {
              if (progressEvent.total) {
                this.uploadProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
              }
            },
          })

          if (response.data?.success) {
            this.$toast.success(response.data.message || 'Firmware uploaded successfully.', 'Success', {
              position: 'topRight',
            })
            this.uploadDialog = false
            this.loadFirmware()
          } else {
            this.$toast.error(response.data?.message || 'Upload failed.', 'Error', {
              position: 'topRight',
            })
          }
        } catch (error) {
          this.$toast.error(
          error?.response?.data?.message || error?.message || 'Upload failed.',
          'Error',
          { position: 'topRight' },
          )
        } finally {
          this.uploading = false
        }
      },

      async updateStatus (item, patch) {
        try {
          const response = await this.$http.patch(`Esp32Ota/Updates/${item.id}/Status`, {
            isActive: patch.isActive !== undefined ? patch.isActive : item.isActive,
            isMandatory: patch.isMandatory !== undefined ? patch.isMandatory : item.isMandatory,
          })

          if (response.data?.success) {
            this.$toast.success('Firmware status updated.', 'Success', {
              position: 'topRight',
            })
          } else {
            this.$toast.error(response.data?.message || 'Could not update status.', 'Error', {
              position: 'topRight',
            })
            this.loadFirmware()
          }
        } catch (error) {
          this.$toast.error(
          error?.response?.data?.message || error?.message || 'Could not update status.',
          'Error',
          { position: 'topRight' },
          )
          this.loadFirmware()
        }
      },

      async deleteFirmware (item) {
        try {
          const response = await this.$http.delete(`Esp32Ota/Updates/${item.id}`)

          if (response.data?.success) {
            this.$toast.success(response.data.message || 'Firmware deleted successfully.', 'Success', {
              position: 'topRight',
            })
            const index = this.firmwareItems.indexOf(item)
            this.firmwareItems.splice(index, 1)
          } else {
            this.$toast.error(response.data?.message || 'Could not delete firmware.', 'Error', {
              position: 'topRight',
            })
          }
        } catch (error) {
          this.$toast.error(
          error?.response?.data?.message || error?.message || 'Could not delete firmware.',
          'Error',
          { position: 'topRight' },
          )
        }
      },

      async loadRecords () {
        this.recordsLoading = true
        try {
          const response = await this.$http.get('Esp32Ota/Records', {
            params: {
              companyId: this.recordsFilter.companyId || undefined,
              deviceId: this.recordsFilter.deviceId || undefined,
              macAddress: this.recordsFilter.macAddress || undefined,
              chipId: this.recordsFilter.chipId || undefined,
              productName: this.recordsFilter.productName || undefined,
            },
          })

          this.recordItems = response.data?.data || response.data || []
        } catch (error) {
          this.$toast.error('Could not load update records.', 'Error', {
            position: 'topRight',
          })
        } finally {
          this.recordsLoading = false
        }
      },
    },
  }
</script>

<style scoped>
  .esp-summary-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
  }

  .esp-summary-card {
    display: flex;
    align-items: center;
    gap: 12px;
    min-height: 82px;
    padding: 16px;
    border: 1px solid #e6e9ef;
    border-radius: 8px;
    background: #fff;
  }

  .version-stack {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    white-space: nowrap;
  }

  .server-url {
    display: inline-block;
    max-width: 280px;
    overflow: hidden;
    text-overflow: ellipsis;
    vertical-align: bottom;
    white-space: nowrap;
  }

  .esp-data-table {
    margin-top: 12px;
  }

  @media (max-width: 960px) {
    .esp-summary-grid {
      grid-template-columns: 1fr;
    }

    .server-url {
      max-width: 180px;
    }
  }
</style>
